import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { Phone, PhoneOff, Video, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const IncomingCall = ({ incomingCall, onAccept, onReject }) => {
  const { t } = useTranslation();
  const [isMobile] = useState(() => window.innerWidth < 768);
  const slideX = useMotionValue(0);
  const slideProgress = useTransform(slideX, [0, 200], [0, 1]);
  const slideOpacity = useTransform(slideX, [0, 150], [1, 0.3]);
  const slideCheckOpacity = useTransform(slideX, [120, 200], [0, 1]);

  // Haptic vibration on mobile
  useEffect(() => {
    if (incomingCall && navigator.vibrate) {
      // Vibration pattern: 200ms buzz, 100ms pause, 200ms buzz — repeating
      const vibratePattern = () => {
        navigator.vibrate([200, 100, 200, 100, 200]);
      };
      vibratePattern();
      const intervalId = setInterval(vibratePattern, 2500);
      return () => {
        clearInterval(intervalId);
        navigator.vibrate(0); // Stop vibration on cleanup
      };
    }
  }, [incomingCall]);

  if (!incomingCall) return null;

  const isVideo = incomingCall.type === 'video';
  const caller = incomingCall.caller || {};
  const callerName = caller.displayName || 'Unknown Caller';
  const callerAvatar = caller.avatar || 'data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><rect width=\'100\' height=\'100\' fill=\'%236366f1\'/><text x=\'50\' y=\'50\' fill=\'white\' font-family=\'sans-serif\' font-size=\'40\' text-anchor=\'middle\' alignment-baseline=\'middle\'>U</text></svg>';
  const isAiAvatar = callerAvatar && (callerAvatar.startsWith('ai-avatar:') || callerAvatar.startsWith('linear-gradient'));
  const avatarStyle = isAiAvatar ? { background: callerAvatar.startsWith('ai-avatar:') ? callerAvatar.substring(10) : callerAvatar } : {};

  const handleSlideEnd = (_, info) => {
    if (info.offset.x > 160) {
      onAccept();
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -60, opacity: 0, scale: 0.92 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: -60, opacity: 0, scale: 0.92 }}
        transition={{ type: 'spring', damping: 24, stiffness: 220 }}
        className="fixed top-6 right-6 z-[120] w-[370px] max-w-[92vw] bg-zinc-950/80 backdrop-blur-2xl border border-white/[0.08] rounded-[20px] p-5 shadow-[0_25px_70px_-15px_rgba(0,0,0,0.75),0_0_0_1px_rgba(255,255,255,0.04)] flex flex-col gap-4 font-sans text-white select-none overflow-hidden"
        data-testid="incoming-call"
      >
        {/* Animated ambient glow background */}
        <motion.div 
          className="absolute inset-0 pointer-events-none rounded-[20px] overflow-hidden"
          animate={{ opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/8 via-transparent to-cyan-500/8" />
          <div className="absolute -top-20 -left-20 w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-cyan-500/8 rounded-full blur-3xl" />
        </motion.div>

        {/* Top row: Avatar + Info + Buttons */}
        <div className="flex items-center gap-4 relative z-10">
          {/* Avatar with pulsing rings */}
          <div className="relative w-14 h-14 flex-shrink-0 flex items-center justify-center">
            {/* Triple pulsing rings */}
            <div className="absolute inset-0 rounded-full border border-emerald-500/20 animate-[ping_2.5s_cubic-bezier(0,0,0.2,1)_infinite]" />
            <div className="absolute inset-0.5 rounded-full border border-teal-400/15 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite_0.3s]" />
            <div className="absolute inset-1 rounded-full border border-emerald-400/10 animate-[ping_1.8s_cubic-bezier(0,0,0.2,1)_infinite_0.6s]" />
            
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white/15 relative z-10 bg-zinc-900 flex items-center justify-center shadow-[0_4px_20px_rgba(0,0,0,0.4)]">
              {isAiAvatar ? (
                <div 
                  style={avatarStyle} 
                  className="w-full h-full flex items-center justify-center text-white font-black text-sm uppercase font-unbounded"
                >
                  {callerName?.substring(0, 2) || 'U'}
                </div>
              ) : (
                <img 
                  src={callerAvatar} 
                  alt={callerName}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.onerror=null; e.target.src='data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><rect width=\'100\' height=\'100\' fill=\'%236366f1\'/><text x=\'50\' y=\'50\' fill=\'white\' font-family=\'sans-serif\' font-size=\'40\' text-anchor=\'middle\' alignment-baseline=\'middle\'>U</text></svg>'; }}
                />
              )}
            </div>
          </div>

          {/* Name and call type */}
          <div className="flex-1 min-w-0 flex flex-col justify-center">
            <h4 className="font-semibold text-[15px] text-white truncate leading-tight tracking-tight mb-1">
              {callerName}
            </h4>
            <p className="text-[9.5px] font-black uppercase tracking-[0.15em] text-emerald-400 flex items-center gap-1.5 font-unbounded">
              <motion.span 
                className="w-2 h-2 rounded-full bg-emerald-400 shrink-0"
                animate={{ 
                  boxShadow: ['0 0 4px #10b981', '0 0 12px #10b981', '0 0 4px #10b981'],
                  scale: [1, 1.15, 1]
                }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
              {isVideo ? t('calls.incomingVideo') || 'Входящий видеовызов' : t('calls.incomingAudio') || 'Входящий аудиовызов'}
            </p>
          </div>

          {/* Decline / Accept buttons — larger for Fitts's Law */}
          <div className="flex items-center gap-3 flex-shrink-0 z-10">
            {/* Decline */}
            <motion.button 
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.88 }}
              onClick={onReject}
              className="w-12 h-12 rounded-full bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/25 hover:border-rose-500/50 text-rose-400 hover:text-rose-300 flex items-center justify-center transition-all shadow-[0_0_20px_rgba(239,68,68,0.1)] backdrop-blur-md"
              title={t('calls.decline') || 'Отклонить'}
            >
              <PhoneOff className="w-5 h-5" />
            </motion.button>

            {/* Accept */}
            {!isMobile && (
              <motion.button 
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.88 }}
                onClick={onAccept}
                className="w-12 h-12 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 hover:border-emerald-500/55 text-emerald-400 hover:text-emerald-300 flex items-center justify-center transition-all shadow-[0_0_20px_rgba(16,185,129,0.12)] backdrop-blur-md"
                title={t('calls.accept') || 'Ответить'}
              >
                {isVideo ? <Video className="w-5 h-5" /> : <Phone className="w-5 h-5 fill-current" />}
              </motion.button>
            )}
          </div>
        </div>

        {/* Mobile: Slide-to-answer gesture */}
        {isMobile && (
          <div className="relative z-10 mt-1">
            <div className="relative h-14 bg-emerald-500/[0.06] backdrop-blur-md rounded-full border border-emerald-500/15 overflow-hidden">
              {/* Background hint text */}
              <motion.div 
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                style={{ opacity: slideOpacity }}
              >
                <div className="flex items-center gap-1.5 text-emerald-400/60">
                  <span className="text-xs font-semibold tracking-wide">{t('calls.slideToAnswer') || 'Сдвиньте чтобы ответить'}</span>
                  <motion.div
                    animate={{ x: [0, 8, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </motion.div>
                </div>
              </motion.div>

              {/* Accepted checkmark */}
              <motion.div 
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                style={{ opacity: slideCheckOpacity }}
              >
                <span className="text-emerald-400 text-xs font-bold">✓ Ответить</span>
              </motion.div>

              {/* Draggable thumb */}
              <motion.div
                drag="x"
                dragConstraints={{ left: 0, right: 200 }}
                dragElastic={0.05}
                dragMomentum={false}
                onDragEnd={handleSlideEnd}
                style={{ x: slideX }}
                className="absolute left-1 top-1 bottom-1 w-12 rounded-full bg-emerald-500/90 flex items-center justify-center text-white shadow-[0_4px_20px_rgba(16,185,129,0.35)] cursor-grab active:cursor-grabbing z-10"
              >
                {isVideo ? <Video className="w-5 h-5" /> : <Phone className="w-5 h-5 fill-current" />}
              </motion.div>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default IncomingCall;
